import { useEffect, useState } from "react";
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrencyEUR, formatDate, formatRelative } from "@/lib/utils";
import { MDF_REQUEST_STATUS_LABEL, type MdfRequestStatus } from "@colab/shared";
import { useMdfRequest } from "./use-mdf-request";
import type { AdminRequestDetail } from "./types";
import { ApproveRequestDialog } from "./approve-request-dialog";
import { DeclineRequestDialog } from "./decline-request-dialog";
import { MarkSpentDialog } from "./mark-spent-dialog";
import { InternalCommentsThread } from "./internal-comments-thread";

// Sub-bucket 15 — right-side request drawer. Used by both the cross-fund
// queue page and the per-fund Requests tab; the parent owns the
// selected-id state and renders the drawer when an id is set.
//
// Status-aware actions:
//   pending  → Approve / Decline
//   approved → Mark spent
//   spent / declined / cancelled → no actions (history-only)
//
// Internal comments thread is always rendered (only blocked for draft + the
// service status gate; the rare admin-on-draft case stays silent).
//
// Legacy used a deep-linkable `?request=<id>` URL pattern, mirrored here so
// the table-row "Open" link can deep-link from anywhere.

const STATUS_VARIANT: Record<
  string,
  "default" | "outline" | "secondary" | "destructive"
> = {
  pending: "outline",
  approved: "default",
  spent: "secondary",
  declined: "destructive",
  cancelled: "outline",
};

function statusLabel(status: string): string {
  if (status in MDF_REQUEST_STATUS_LABEL) {
    return MDF_REQUEST_STATUS_LABEL[status as MdfRequestStatus];
  }
  if (status === "cancelled") return "Cancelled";
  return status;
}

export function RequestDrawer({
  requestId,
  onClose,
}: {
  requestId: string | null;
  onClose: () => void;
}) {
  const open = !!requestId;
  const query = useMdfRequest(requestId);

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px]"
      >
        {!requestId ? null : query.isLoading ? (
          <DrawerSkeleton />
        ) : query.isError || !query.data ? (
          <div className="p-6">
            <SheetHeader>
              <SheetTitle>Couldn&apos;t load request</SheetTitle>
              <SheetDescription>
                {query.error?.message ?? "The request may no longer exist."}
              </SheetDescription>
            </SheetHeader>
          </div>
        ) : (
          <DrawerBody detail={query.data} requestId={requestId} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-3 w-1/3" />
      <Separator className="my-3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function DrawerBody({
  detail,
  requestId,
}: {
  detail: AdminRequestDetail;
  requestId: string;
}) {
  const { request, fund, partner, reviewer, supporting_doc_signed_url, attachments } =
    detail;
  const fundId = request.fund_id;
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [spentOpen, setSpentOpen] = useState(false);

  // Close inner dialogs if the drawer scrolls to a different request.
  useEffect(() => {
    setApproveOpen(false);
    setDeclineOpen(false);
    setSpentOpen(false);
  }, [requestId]);

  const showApprove = request.status === "pending";
  const showDecline = request.status === "pending";
  const showSpent = request.status === "approved";
  const allowComments = ["pending", "approved", "declined"].includes(
    request.status,
  );

  return (
    <>
      <SheetHeader className="flex flex-row items-start justify-between gap-3 border-b p-6 pb-5 text-left">
        <div className="min-w-0">
          <SheetTitle className="font-display text-lg font-semibold tracking-tight">
            {request.campaign_name}
          </SheetTitle>
          <SheetDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={STATUS_VARIANT[request.status] ?? "outline"}>
              {statusLabel(request.status)}
            </Badge>
            <span className="text-muted-foreground">
              · {partner?.company_name ?? "Unknown partner"} ·{" "}
              {fund?.name ?? "Unknown fund"}
            </span>
          </SheetDescription>
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
        {(showApprove || showDecline || showSpent) && (
          <section className="flex flex-wrap gap-2">
            {showApprove && (
              <Button size="sm" onClick={() => setApproveOpen(true)}>
                Approve request
              </Button>
            )}
            {showDecline && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setDeclineOpen(true)}
              >
                Decline…
              </Button>
            )}
            {showSpent && (
              <Button size="sm" onClick={() => setSpentOpen(true)}>
                Mark spent
              </Button>
            )}
          </section>
        )}

        <section className="grid gap-4 rounded-xl border bg-card p-4">
          <Row label="Amount">
            <span className="font-display text-xl font-semibold tabular-nums">
              {formatCurrencyEUR(request.amount_eur)}
            </span>
          </Row>
          <Row label="Campaign type">{request.campaign_type ?? "—"}</Row>
          <Row label="Submitted">
            {formatDate(request.created_at)}{" "}
            <span className="text-muted-foreground">
              · {formatRelative(request.created_at)}
            </span>
          </Row>
          {request.approved_at && (
            <Row label="Approved">
              {formatDate(request.approved_at)}
              {reviewer?.full_name && (
                <span className="text-muted-foreground">
                  {" "}
                  · by {reviewer.full_name}
                </span>
              )}
            </Row>
          )}
          {request.spent_at && (
            <Row label="Marked spent">{formatDate(request.spent_at)}</Row>
          )}
        </section>

        {request.description && (
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Description
            </h3>
            <p className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
              {request.description}
            </p>
          </section>
        )}

        {(attachments.length > 0 || supporting_doc_signed_url) && (
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Receipts &amp; attachments
            </h3>
            <ul className="space-y-1.5">
              {supporting_doc_signed_url && (
                <li>
                  <a
                    href={supporting_doc_signed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Supporting document</span>
                    <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                  </a>
                </li>
              )}
              {attachments.map((a) => (
                <li key={a.id}>
                  {a.signed_url ? (
                    <a
                      href={a.signed_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted"
                    >
                      <Download className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{a.file_name}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        {formatBytes(a.size_bytes)}
                      </span>
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {a.file_name}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {request.declined_reason && (
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Decline reason
            </h3>
            <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm italic">
              &ldquo;{request.declined_reason}&rdquo;
            </p>
          </section>
        )}

        {allowComments && (
          <>
            <Separator />
            <InternalCommentsThread requestId={requestId} />
          </>
        )}
      </div>

      {approveOpen && (
        <ApproveRequestDialog
          requestId={requestId}
          fundId={fundId}
          open={approveOpen}
          onOpenChange={setApproveOpen}
        />
      )}
      {declineOpen && (
        <DeclineRequestDialog
          requestId={requestId}
          fundId={fundId}
          open={declineOpen}
          onOpenChange={setDeclineOpen}
        />
      )}
      {spentOpen && (
        <MarkSpentDialog
          requestId={requestId}
          fundId={fundId}
          open={spentOpen}
          onOpenChange={setSpentOpen}
        />
      )}
    </>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-baseline gap-3 text-sm">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
